import React, { useState, useEffect, useMemo } from "react";

import {
  Button,
  Card,
  FormLayout,
  Modal,
  Stack,
  Tag,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import { isValidWeightDescriptor } from "util/weightValidation";

/**
 * Department default weights section for the Weight Options page.
 *
 * Renders a Polaris Card listing the three weighted departments (Produce, Meat,
 * Deli). Each row shows the current weight Tags and a "Manage" button that
 * opens a per-department Modal to add/remove weights and save changes.
 *
 * Props
 * -----
 * @param {object}   shopConfig  - The shop_config object. Reads
 *                                 `globepos_produce_variant_weights`,
 *                                 `globepos_meat_variant_weights`, and
 *                                 `globepos_deli_variant_weights` from it.
 *                                 Missing / non-array values are treated as [].
 * @param {function} onSave      - Called with a partial shop_config object when
 *                                 the user saves a department, e.g.
 *                                 `{ globepos_meat_variant_weights: ["250g"] }`.
 *                                 May return a Promise; if it does, the modal
 *                                 primary action enters a loading state until
 *                                 the Promise settles, then closes on success.
 */
export default function DepartmentVariantWeightsSection({
  shopConfig,
  onSave,
  onReset,
}) {
  const DEPARTMENTS = useMemo(
    () => [
      {
        key: "produce",
        label: "Produce",
        field: "globepos_produce_variant_weights",
      },
      {
        key: "meat",
        label: "Meat",
        field: "globepos_meat_variant_weights",
      },
      {
        key: "deli",
        label: "Deli",
        field: "globepos_deli_variant_weights",
      },
    ],
    [],
  );

  // Which department's modal is open (dept.key string) or null.
  const [activeDeptKey, setActiveDeptKey] = useState(null);

  // Working copy of weights for the open modal.
  const [weightsDraft, setWeightsDraft] = useState([]);

  // New-weight text field value.
  const [newWeight, setNewWeight] = useState("");

  // Inline error on the text field.
  const [fieldError, setFieldError] = useState(null);

  // True while the onSave Promise is pending.
  const [saving, setSaving] = useState(false);

  const activeDept = useMemo(
    () => DEPARTMENTS.find((d) => d.key === activeDeptKey) || null,
    [DEPARTMENTS, activeDeptKey],
  );

  // Seed the draft from shopConfig each time the modal opens.
  useEffect(() => {
    if (!activeDept) return;
    const existing = Array.isArray(shopConfig?.[activeDept.field])
      ? shopConfig[activeDept.field]
      : [];
    setWeightsDraft(existing);
    setNewWeight("");
    setFieldError(null);
  }, [activeDept, shopConfig]);

  const openDepartment = (key) => {
    setActiveDeptKey(key);
  };

  const closeModal = () => {
    if (saving) return; // don't allow close mid-save
    setActiveDeptKey(null);
    setWeightsDraft([]);
    setNewWeight("");
    setFieldError(null);
  };

  const handleAddWeight = () => {
    const trimmed = newWeight.trim();
    if (!trimmed) return;

    if (!isValidWeightDescriptor(trimmed)) {
      setFieldError("Use a format like 1kg, 500g, or 1lb");
      return;
    }

    // Silently ignore exact duplicates (case-insensitive).
    if (weightsDraft.some((w) => w.toLowerCase() === trimmed.toLowerCase())) {
      setNewWeight("");
      setFieldError(null);
      return;
    }

    setWeightsDraft((prev) => [...prev, trimmed]);
    setNewWeight("");
    setFieldError(null);
  };

  const handleRemoveWeight = (descriptor) => {
    setWeightsDraft((prev) => prev.filter((w) => w !== descriptor));
  };

  const handleSave = () => {
    if (!activeDept) return;

    const partialConfig = { [activeDept.field]: weightsDraft };
    const result = onSave(partialConfig);

    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          setActiveDeptKey(null);
        })
        .catch(() => {
          // Leave modal open so the user can retry; parent is responsible for
          // surfacing the error to the user (e.g. via a banner).
          setSaving(false);
        });
    } else {
      // Synchronous onSave — close immediately.
      setActiveDeptKey(null);
    }
  };

  const handleNewWeightChange = (value) => {
    setNewWeight(value);
    if (fieldError) setFieldError(null);
  };

  return (
    <>
      <Card>
        <Card.Header title="Department default weights" />
        <Card.Section>
          <TextStyle variation="subdued">
            These weight variants apply to all weighted products in a department
            during import. They are overridden by any per-item weight settings
            you configure below.
          </TextStyle>
        </Card.Section>
        <Card.Section>
          <Stack vertical spacing="loose">
            {DEPARTMENTS.map((dept) => {
              const currentWeights = Array.isArray(shopConfig?.[dept.field])
                ? shopConfig[dept.field]
                : [];

              return (
                <Stack key={dept.key} alignment="center" distribution="equalSpacing">
                  <Stack.Item fill>
                    <TextStyle variation="strong">{dept.label}</TextStyle>
                    <div style={{ marginTop: 4 }}>
                      {currentWeights.length > 0 ? (
                        <Stack spacing="extraTight" wrap>
                          {currentWeights.map((w) => (
                            <Tag key={`${dept.key}-${w}`}>{w}</Tag>
                          ))}
                        </Stack>
                      ) : (
                        <TextStyle variation="subdued">Using defaults</TextStyle>
                      )}
                    </div>
                  </Stack.Item>
                  <Stack spacing="tight">
                    {currentWeights.length > 0 && onReset && (
                      <Button size="slim" onClick={() => onReset(dept.field)}>
                        Reset to defaults
                      </Button>
                    )}
                    <Button size="slim" onClick={() => openDepartment(dept.key)}>
                      Manage
                    </Button>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Card.Section>
      </Card>

      <Modal
        open={Boolean(activeDeptKey)}
        title={activeDept ? `Manage ${activeDept.label} weights` : "Manage weights"}
        onClose={closeModal}
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Add the weight variants to expose when a matching department item
              is imported. Remove a weight by clicking the × on its chip.
            </TextStyle>

            <Stack spacing="tight" wrap>
              {weightsDraft.length === 0 ? (
                <TextStyle variation="subdued">No custom weights added.</TextStyle>
              ) : (
                weightsDraft.map((descriptor) => (
                  <Tag
                    key={`${activeDeptKey}-${descriptor}`}
                    onRemove={() => handleRemoveWeight(descriptor)}
                  >
                    {descriptor}
                  </Tag>
                ))
              )}
            </Stack>

            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Add weight"
                  value={newWeight}
                  onChange={handleNewWeightChange}
                  autoComplete="off"
                  placeholder="e.g. 1kg, 500g, 1lb"
                  error={fieldError}
                />
                <Button onClick={handleAddWeight} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}
